import { useState } from "react";
import { Copy, Check, Loader2, Mail, Key } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { Button } from "@/dentlab/src/components/ui/button";

interface DevActivationCodeGeneratorProps {
    onCodeGenerated: (code: string) => void;
    onEmailGenerated: (email: string) => void;
}

export const DevActivationCodeGenerator: React.FC<DevActivationCodeGeneratorProps> = ({
    onCodeGenerated,
    onEmailGenerated,
}) => {
    const [codeLoading, setCodeLoading] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);

    const handleGenerateCode = async () => {
        setCodeLoading(true);
        try {
            const { data, error } = await supabase.rpc('create_activation_code_e2e', {
                p_comment: 'Dev UI generated'
            });
            if (error) throw error;
            onCodeGenerated(data);
            await navigator.clipboard.writeText(data);
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        } catch (error) {
            Logger.error(error, {}, "Failed to generate dev activation code");
        } finally {
            setCodeLoading(false);
        }
    };

    const handleGenerateEmail = async () => {
        const uuid = crypto.randomUUID();
        const email = `julius.janda+${uuid}@dentdesk.ch`;
        onEmailGenerated(email);
        await navigator.clipboard.writeText(email);
        setEmailCopied(true);
        setTimeout(() => setEmailCopied(false), 2000);
    };

    return (
        <div className="flex gap-2">
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateEmail}
                className="flex-1 border-dashed border-orange-300 text-orange-600 hover:bg-orange-50"
            >
                {emailCopied ? (
                    <Check className="h-4 w-4 mr-2" />
                ) : (
                    <Mail className="h-4 w-4 mr-2" />
                )}
                {emailCopied ? "Kopiert!" : "Email"}
            </Button>
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateCode}
                disabled={codeLoading}
                className="flex-1 border-dashed border-orange-300 text-orange-600 hover:bg-orange-50"
            >
                {codeLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : codeCopied ? (
                    <Check className="h-4 w-4 mr-2" />
                ) : (
                    <Key className="h-4 w-4 mr-2" />
                )}
                {codeCopied ? "Kopiert!" : "Code"}
            </Button>
        </div>
    );
};
